(() => {
  const motion = window.PerfumePalaceMotion;
  const arabicScriptPattern = /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff\ufb50-\ufdff\ufe70-\ufeff]/;

  const debounce = (callback, wait = 220) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  };

  const resolveContentDirection = (element) => {
    const text = element.textContent?.trim();
    if (!text) return;
    const explicitDirection = element.getAttribute('dir');
    const isMarkedContent = element.hasAttribute('data-pp-bidi-content');
    if (!isMarkedContent && (explicitDirection === 'rtl' || explicitDirection === 'ltr')) return;

    const direction = arabicScriptPattern.test(text) ? 'rtl' : 'ltr';
    element.setAttribute('dir', direction);
    element.dataset.ppBidiResolved = direction;
  };

  const initMerchantCopyDirection = (scope = document) => {
    const directContent = [
      ...(scope.matches?.('[data-pp-bidi-content]') ? [scope] : []),
      ...scope.querySelectorAll('[data-pp-bidi-content]')
    ];
    directContent.forEach(resolveContentDirection);

    const richTextRoots = [
      ...(scope.matches?.('[data-pp-bidi-richtext]') ? [scope] : []),
      ...scope.querySelectorAll('[data-pp-bidi-richtext]')
    ];
    richTextRoots.forEach((root) => {
      const blocks = root.querySelectorAll('p, li, h2, h3, h4, blockquote, dd');
      if (blocks.length === 0) resolveContentDirection(root);
      blocks.forEach(resolveContentDirection);
    });
  };

  const initCollection = (scope = document) => {
    scope.querySelectorAll('[data-pp-sort-select]').forEach((select) => {
      if (select.dataset.ppSortReady === 'true') return;
      select.dataset.ppSortReady = 'true';
      select.addEventListener('change', () => {
        if (select.closest('[data-pp-filter-drawer]')) return;
        select.form?.submit();
      });
    });

    const sections = [
      ...(scope.matches?.('[data-section-type="collection-template"]') ? [scope] : []),
      ...scope.querySelectorAll('[data-section-type="collection-template"]')
    ];

    sections.forEach((section) => {
      if (section.dataset.ppFilterDrawerReady === 'true') return;
      const drawer = section.querySelector('[data-pp-filter-drawer]');
      const openButton = section.querySelector('[data-pp-filter-open]');
      const backdrop = section.querySelector('.pp-filter-backdrop');
      if (!drawer || !openButton || !backdrop) return;

      section.dataset.ppFilterDrawerReady = 'true';
      const drawerMode = window.matchMedia('(min-width: 0px)');
      const closeButtons = section.querySelectorAll('[data-pp-filter-close]');
      const filterForm = drawer.querySelector('form');
      const sortField = section.querySelector('[data-pp-sort-select]')?.closest('.pp-field');
      const sortParent = sortField?.parentNode;
      const sortNextSibling = sortField?.nextSibling;
      const minimumPrice = filterForm?.querySelector('input[name="filter.v.price.gte"]');
      const maximumPrice = filterForm?.querySelector('input[name="filter.v.price.lte"]');
      let isOpen = false;

      const getFocusable = () => [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getClientRects().length > 0);

      const closeDrawer = (restoreFocus = true) => {
        isOpen = false;
        if (sortField && sortParent) sortParent.insertBefore(sortField, sortNextSibling);
        drawer.classList.remove('is-open');
        backdrop.classList.remove('is-open');
        document.body.classList.remove('pp-filter-drawer-open');
        openButton.setAttribute('aria-expanded', 'false');
        if (drawerMode.matches) {
          drawer.setAttribute('aria-hidden', 'true');
          drawer.inert = true;
        }
        if (restoreFocus && drawerMode.matches) openButton.focus({ preventScroll: true });
      };

      const openDrawer = () => {
        if (!drawerMode.matches) return;
        isOpen = true;
        if (sortField && filterForm) filterForm.prepend(sortField);
        drawer.inert = false;
        drawer.setAttribute('aria-hidden', 'false');
        drawer.scrollTop = 0;
        drawer.classList.add('is-open');
        backdrop.classList.add('is-open');
        document.body.classList.add('pp-filter-drawer-open');
        openButton.setAttribute('aria-expanded', 'true');
        window.requestAnimationFrame(() => drawer.querySelector('[data-pp-filter-close]')?.focus({ preventScroll: true }));
      };

      const syncViewport = () => {
        if (drawerMode.matches) {
          drawer.setAttribute('role', 'dialog');
          drawer.setAttribute('aria-modal', 'true');
          if (!isOpen) closeDrawer(false);
          return;
        }

        isOpen = false;
        drawer.classList.remove('is-open');
        backdrop.classList.remove('is-open');
        drawer.removeAttribute('role');
        drawer.removeAttribute('aria-modal');
        drawer.removeAttribute('aria-hidden');
        drawer.inert = false;
        document.body.classList.remove('pp-filter-drawer-open');
        openButton.setAttribute('aria-expanded', 'false');
      };

      openButton.addEventListener('click', openDrawer);
      closeButtons.forEach((button) => button.addEventListener('click', () => closeDrawer()));
      [minimumPrice, maximumPrice].filter(Boolean).forEach((input) => input.addEventListener('input', () => {
        minimumPrice?.setCustomValidity('');
        maximumPrice?.setCustomValidity('');
      }));
      filterForm?.addEventListener('submit', (event) => {
        const minimum = minimumPrice?.value === '' ? null : Number(minimumPrice?.value);
        const maximum = maximumPrice?.value === '' ? null : Number(maximumPrice?.value);
        if (minimum !== null && maximum !== null && minimum > maximum) {
          event.preventDefault();
          maximumPrice.setCustomValidity(filterForm.dataset.ppInvalidPriceRange || 'Maximum price must be greater than or equal to minimum price.');
          maximumPrice.reportValidity();
          maximumPrice.focus({ preventScroll: true });
        }
      });
      drawer.addEventListener('keydown', (event) => {
        if (!isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      drawerMode.addEventListener?.('change', syncViewport);
      syncViewport();
    });
  };

  const initCollectionPagination = (scope = document) => {
    const roots = [
      ...(scope.matches?.('[data-pp-collection-pagination]') ? [scope] : []),
      ...scope.querySelectorAll('[data-pp-collection-pagination]')
    ];
    roots.forEach((root) => {
      if (root.dataset.ppInfiniteReady === 'true' || !('IntersectionObserver' in window)) return;
      const grid = document.getElementById(root.dataset.gridId);
      const link = root.querySelector('[data-pp-collection-next]');
      const status = root.querySelector('[data-pp-collection-load-status]');
      if (!grid || !status || !link) return;
      root.dataset.ppInfiniteReady = 'true';
      root.dataset.ppAutoLoad = 'true';
      const visited = new Set();
      let pending = false;
      let destroyed = false;
      let controller;
      const setStatus = (text, visible) => {
        status.textContent = text;
        status.classList.toggle('pp-visually-hidden', !visible);
      };
      const pageUrl = (href) => {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) throw new Error('Unexpected collection page');
        url.hash = '';
        url.searchParams.delete('section_id');
        return url;
      };
      const productKey = (card) => {
        const href = card.querySelector('.pp-v2-product-card__title a[href]')?.getAttribute('href');
        return href ? new URL(href, window.location.href).pathname : '';
      };
      const finish = () => {
        root.dataset.ppLoadState = 'complete';
        link.hidden = true;
        observer.disconnect();
        setStatus(root.dataset.completeLabel, true);
      };
      const load = async () => {
        if (pending || destroyed || link.hidden) return;
        pending = true;
        observer.unobserve(root);
        root.dataset.ppLoadState = 'loading';
        grid.setAttribute('aria-busy', 'true');
        link.setAttribute('aria-disabled', 'true');
        setStatus(root.dataset.loadingLabel, true);
        controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
          const url = pageUrl(link.href);
          const key = url.href;
          if (visited.has(key)) throw new Error('Repeated collection page');
          url.searchParams.set('section_id', root.dataset.sectionId);
          const response = await fetch(url.href, {signal: controller.signal, headers: {'X-Requested-With': 'XMLHttpRequest'}});
          if (!response.ok) throw new Error('Collection page unavailable');
          const html = await response.text();
          if (destroyed || !root.isConnected) return;
          const nextDocument = new DOMParser().parseFromString(html, 'text/html');
          const nextGrid = nextDocument.getElementById(grid.id);
          if (!nextGrid || !nextGrid.querySelector('[data-pp-product-card]')) throw new Error('Missing collection products');
          const nextRoot = nextGrid.closest('[data-section-type="collection-template"]')?.querySelector('[data-pp-collection-pagination]');
          if (!nextRoot || nextRoot.dataset.gridId !== grid.id) throw new Error('Missing collection pagination');
          const nextHref = nextRoot?.querySelector('[data-pp-collection-next]')?.getAttribute('href');
          const nextUrl = nextHref ? pageUrl(nextHref) : null;
          if (nextUrl && (nextUrl.href === key || visited.has(nextUrl.href))) throw new Error('Repeated next collection page');
          const known = new Set([...grid.querySelectorAll('[data-pp-product-card]')].map(productKey));
          const fragment = document.createDocumentFragment();
          let count = 0;
          nextGrid.querySelectorAll('[data-pp-product-card]').forEach((card) => {
            const product = productKey(card);
            if (!product || known.has(product)) return;
            known.add(product);
            count += 1;
            const imported = document.importNode(card, true);
            imported.querySelectorAll('img').forEach((image) => { image.loading = 'lazy'; image.fetchPriority = 'low'; });
            fragment.append(imported);
            const dialogs = new Set([...card.querySelectorAll('[data-pp-open]')].map(button => button.getAttribute('data-pp-open')?.slice(1)).filter(Boolean));
            dialogs.forEach((id) => {
              const dialog = nextDocument.getElementById(id);
              if (!dialog?.matches('dialog[data-pp-card-choice]')) throw new Error('Missing product choice dialog');
              if (!document.getElementById(id)) {
                fragment.append(document.importNode(dialog, true));
              }
            });
          });
          grid.append(fragment);
          visited.add(key);
          document.dispatchEvent(new CustomEvent('pp:products:loaded', {detail: {scope: grid}}));
          if (!nextUrl) finish();
          else {
            link.href = nextUrl.href;
            root.dataset.ppLoadState = 'ready';
            setStatus(root.dataset.loadedLabel.replace('[count]', String(count)), false);
            observer.observe(root);
          }
        } catch (error) {
          if (!destroyed) {
            root.dataset.ppLoadState = 'error';
            setStatus(root.dataset.errorLabel, true);
          }
        } finally {
          window.clearTimeout(timeout);
          pending = false;
          grid.setAttribute('aria-busy', 'false');
          link.removeAttribute('aria-disabled');
        }
      };
      const observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting) && root.dataset.ppLoadState !== 'error') load();
      }, {rootMargin: '600px 0px'});
      const onClick = (event) => {
        if (root.dataset.ppLoadState === 'error') return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        load();
      };
      link.addEventListener('click', onClick);
      root.ppCollectionPaginationDestroy = () => {
        destroyed = true;
        observer.disconnect();
        controller?.abort();
        link.removeEventListener('click', onClick);
        delete root.dataset.ppInfiniteReady;
        delete root.dataset.ppAutoLoad;
      };
      observer.observe(root);
    });
  };

  const pauseMedia = (container) => {
    container?.querySelectorAll('video').forEach((video) => video.pause());
  };

  const initProductGallery = (gallery) => {
    if (!gallery || gallery.dataset.ppGalleryReady === 'true') return;
    gallery.dataset.ppGalleryReady = 'true';

    const stage = gallery.querySelector('[data-pp-gallery-stage]');
    const media = [...gallery.querySelectorAll('[data-pp-media-id]')];
    const dialog = gallery.querySelector('[data-pp-gallery-dialog]');
    const zoomButton = dialog?.querySelector('[data-pp-gallery-zoom]');
    const reducedMotion = motion?.reducedMotion || window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeIndex = Math.max(0, media.findIndex((item) => item.classList.contains('is-active')));
    let scrollFrame;
    let dialogTrigger;

    const setZoom = (zoomed) => {
      if (!dialog || !zoomButton) return;
      const activeLightboxMedia = dialog.querySelector('[data-pp-lightbox-media-id].is-active');
      const canZoom = activeLightboxMedia?.dataset.mediaType === 'image';
      const nextZoomed = Boolean(zoomed && canZoom);
      dialog.classList.toggle('is-zoomed', nextZoomed);
      zoomButton.hidden = !canZoom;
      zoomButton.setAttribute('aria-pressed', String(nextZoomed));
      const label = nextZoomed ? zoomButton.dataset.zoomOutLabel : zoomButton.dataset.zoomInLabel;
      zoomButton.setAttribute('aria-label', label);
      zoomButton.setAttribute('title', label);
    };

    const setMediaState = (item, active, loadVideo = false) => {
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
      item.inert = !active;
      if (active && loadVideo) {
        item.querySelectorAll('video[preload="none"]').forEach((video) => {
          video.preload = 'metadata';
        });
      } else if (!active) {
        pauseMedia(item);
      }
    };

    const updateMedia = (mediaId, options = {}) => {
      const nextIndex = media.findIndex((item) => item.dataset.ppMediaId === String(mediaId));
      if (nextIndex < 0) return;
      activeIndex = nextIndex;
      const activeId = media[activeIndex].dataset.ppMediaId;

      media.forEach((item, index) => {
        const active = index === activeIndex;
        setMediaState(item, active, true);
      });
      gallery.querySelectorAll('[data-pp-gallery-thumb], [data-pp-gallery-dot]').forEach((control) => {
        control.setAttribute('aria-current', String(control.dataset.mediaTarget === activeId));
      });
      gallery.querySelectorAll('[data-pp-lightbox-media-id]').forEach((item) => {
        const active = item.dataset.ppLightboxMediaId === activeId;
        setMediaState(item, active, Boolean(dialog?.open));
      });
      gallery.querySelectorAll('[data-pp-gallery-counter]').forEach((counter) => {
        counter.textContent = `${activeIndex + 1} / ${media.length}`;
      });
      setZoom(false);

      if (options.scroll && stage && window.matchMedia('(max-width: 749px)').matches) {
        stage.scrollTo({
          left: activeIndex * stage.clientWidth,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }
      if (options.focus) {
        const target = media[activeIndex];
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    };

    const move = (offset) => {
      if (media.length < 2) return;
      const nextIndex = (activeIndex + offset + media.length) % media.length;
      updateMedia(media[nextIndex].dataset.ppMediaId, { scroll: !dialog?.open });
    };

    gallery.querySelectorAll('[data-pp-gallery-thumb], [data-pp-gallery-dot]').forEach((control) => {
      control.addEventListener('click', () => updateMedia(control.dataset.mediaTarget, { scroll: true }));
    });
    gallery.querySelectorAll('[data-pp-gallery-previous]').forEach((button) => {
      button.addEventListener('click', () => move(-1));
    });
    gallery.querySelectorAll('[data-pp-gallery-next]').forEach((button) => {
      button.addEventListener('click', () => move(1));
    });

    stage?.addEventListener('scroll', () => {
      if (!window.matchMedia('(max-width: 749px)').matches || scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null;
        const index = Math.max(0, Math.min(media.length - 1, Math.round(stage.scrollLeft / stage.clientWidth)));
        if (index !== activeIndex) updateMedia(media[index].dataset.ppMediaId);
      });
    }, { passive: true });

    gallery.querySelector('[data-pp-gallery-open]')?.addEventListener('click', (event) => {
      if (!dialog) return;
      dialogTrigger = event.currentTarget;
      dialog.showModal();
      updateMedia(media[activeIndex].dataset.ppMediaId);
      dialog.querySelector('[data-pp-gallery-close]')?.focus({ preventScroll: true });
    });
    dialog?.querySelector('[data-pp-gallery-close]')?.addEventListener('click', () => dialog.close());
    zoomButton?.addEventListener('click', () => setZoom(!dialog.classList.contains('is-zoomed')));
    dialog?.addEventListener('close', () => {
      pauseMedia(dialog);
      setZoom(false);
      dialogTrigger?.focus({ preventScroll: true });
    });
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    gallery.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('video, iframe, model-viewer, input, select, textarea')) return;
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
    });

    const alignMobileStage = () => {
      if (!stage || !window.matchMedia('(max-width: 749px)').matches) return;
      stage.scrollTo({ left: activeIndex * stage.clientWidth, behavior: 'auto' });
    };
    window.addEventListener('resize', alignMobileStage, { passive: true });
    alignMobileStage();
    gallery.ppSetMedia = updateMedia;
    if (media[activeIndex]) updateMedia(media[activeIndex].dataset.ppMediaId);
  };

  const activateMedia = (gallery, mediaId, shouldFocus = false) => {
    if (!gallery || !mediaId) return;
    gallery.ppSetMedia?.(mediaId, { scroll: true, focus: shouldFocus });
  };

  const initPurchaseBehavior = (productPage, form) => {
    if (productPage.dataset.ppPurchaseReady === 'true') return;
    productPage.dataset.ppPurchaseReady = 'true';

    const panel = productPage.querySelector('[data-pp-purchase-panel]');
    const decisionColumn = productPage.querySelector('.pp-product-page__decision-column');
    const layout = productPage.querySelector('.pp-product-page__layout');
    const mainButton = form?.querySelector('[data-pp-pdp-add]');
    const variantSelect = form?.querySelector('[data-pp-pdp-variant]');
    const mobileBar = productPage.querySelector('[data-pp-mobile-buy-bar]');
    const mobileButton = mobileBar?.querySelector('[data-pp-mobile-buy-button]');
    const footer = document.querySelector('footer');
    let mainButtonVisible = true;
    let mainButtonSeen = false;
    let footerVisible = false;
    let purchaseFrame;
    let panelNaturalOffset = panel?.offsetTop || 0;

    const updateDesktopPanel = () => {
      if (!panel || !decisionColumn || !layout) return;
      const desktop = window.matchMedia('(min-width: 1100px)').matches;
      const fitsViewport = panel.offsetHeight <= window.innerHeight - 144;
      if (!desktop || !fitsViewport) {
        panel.classList.remove('is-pinned', 'is-released');
        panel.style.removeProperty('--pp-purchase-left');
        panel.style.removeProperty('--pp-purchase-width');
        return;
      }

      if (!panel.classList.contains('is-pinned') && !panel.classList.contains('is-released')) {
        panelNaturalOffset = panel.offsetTop;
      }
      const columnRect = decisionColumn.getBoundingClientRect();
      const layoutRect = layout.getBoundingClientRect();
      const startY = window.scrollY + layoutRect.top + panelNaturalOffset - 128;
      const endY = window.scrollY + layoutRect.bottom - panel.offsetHeight - 128;
      panel.style.setProperty('--pp-purchase-left', `${columnRect.left}px`);
      panel.style.setProperty('--pp-purchase-width', `${columnRect.width}px`);

      if (window.scrollY >= endY) {
        panel.classList.remove('is-pinned');
        panel.classList.add('is-released');
      } else if (window.scrollY >= startY) {
        panel.classList.remove('is-released');
        panel.classList.add('is-pinned');
      } else {
        panel.classList.remove('is-pinned', 'is-released');
      }
    };

    const requestPurchaseUpdate = () => {
      if (purchaseFrame) return;
      purchaseFrame = window.requestAnimationFrame(() => {
        purchaseFrame = null;
        updateDesktopPanel();
      });
    };
    const updateMobileBar = () => {
      if (!mobileBar) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const dialogOpen = Boolean(document.querySelector('dialog[open]'));
      const visible = mobile && mainButtonSeen && !mainButtonVisible && !footerVisible && !dialogOpen;
      mobileBar.classList.toggle('is-visible', visible);
      mobileBar.setAttribute('aria-hidden', String(!visible));
      mobileBar.inert = !visible;
    };

    if ('IntersectionObserver' in window) {
      if (mainButton) {
        const buttonObserver = new IntersectionObserver(([entry]) => {
          mainButtonVisible = entry.isIntersecting;
          if (entry.isIntersecting) mainButtonSeen = true;
          updateMobileBar();
        }, { threshold: 0.05 });
        buttonObserver.observe(mainButton);
      }
      if (footer) {
        const footerObserver = new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          updateMobileBar();
        }, { rootMargin: '160px 0px 0px' });
        footerObserver.observe(footer);
      }
    }

    const dialogObserver = new MutationObserver(updateMobileBar);
    dialogObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });

    mobileButton?.addEventListener('click', () => {
      const option = variantSelect?.selectedOptions?.[0];
      const valid = variantSelect ? Boolean(option?.value && option.dataset.available === 'true') : !mainButton?.disabled;
      if (!valid) {
        const fallbackVisible = variantSelect && !variantSelect.hidden;
        const target = fallbackVisible
          ? variantSelect
          : productPage.querySelector('[data-pp-variant-option]:not(:disabled)') || mainButton;
        if (target && !target.matches('button, input, select, textarea, a[href], [tabindex]')) target.setAttribute('tabindex', '-1');
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => target?.focus({ preventScroll: true }), 280);
        return;
      }
      mainButton?.click();
    });

    window.addEventListener('resize', () => {
      requestPurchaseUpdate();
      updateMobileBar();
    }, { passive: true });
    window.addEventListener('scroll', requestPurchaseUpdate, { passive: true });
    updateDesktopPanel();
    updateMobileBar();
  };

  const initProduct = (scope = document) => {
    scope.querySelectorAll('[data-pp-product-page]').forEach((productPage) => {
      const gallery = productPage.querySelector('[data-pp-product-gallery]');
      const form = productPage.querySelector('[data-pp-pdp-form]');
      const variantSelect = form?.querySelector('[data-pp-pdp-variant]');
      const price = productPage.querySelector('[data-pp-pdp-price]');
      const comparePrice = productPage.querySelector('[data-pp-pdp-compare]');
      const saleBadge = productPage.querySelector('[data-pp-sale-badge]');
      const savingsPill = productPage.querySelector('[data-pp-savings-pill]');
      const savingsLabel = savingsPill?.querySelector('[data-pp-savings-label]');
      const addButton = form?.querySelector('[data-pp-pdp-add]');
      const buyNowButton = form?.querySelector('[data-pp-buy-now]');
      const mobilePrice = productPage.querySelector('[data-pp-mobile-buy-price]');
      const mobileButton = productPage.querySelector('[data-pp-mobile-buy-button]');
      const variantButtons = productPage.querySelector('[data-pp-variant-buttons]');
      const variantOptions = [...productPage.querySelectorAll('[data-pp-variant-option]')];

      initProductGallery(gallery);
      initPurchaseBehavior(productPage, form);

      variantOptions.forEach((button) => {
        button.addEventListener('click', () => {
          if (!variantSelect || button.disabled) return;
          variantSelect.value = button.dataset.value;
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      const updateVariantState = ({ updateUrl = true, updateMedia = true } = {}) => {
        const option = variantSelect.selectedOptions[0];
        const available = option?.dataset.available === 'true';
        if (!option) return;

        window.PerfumePalacePrice?.set(price, option.dataset.priceCents, option.dataset.price || '');
        window.PerfumePalacePrice?.set(mobilePrice, option.dataset.priceCents, option.dataset.price || '');
        if (comparePrice) {
          window.PerfumePalacePrice?.set(comparePrice, option.dataset.comparePriceCents, option.dataset.comparePrice || '');
          comparePrice.hidden = !option.dataset.comparePrice;
        }
        const hasDiscount = Boolean(option.dataset.comparePrice && option.dataset.savingsLabel);
        if (savingsPill) savingsPill.hidden = !hasDiscount;
        if (savingsLabel && hasDiscount) savingsLabel.textContent = option.dataset.savingsLabel;
        if (saleBadge) {
          saleBadge.hidden = !hasDiscount;
          if (hasDiscount) saleBadge.textContent = (saleBadge.dataset.labelTemplate || '__PERCENT__').replace('__PERCENT__', option.dataset.discountPercent || '0');
        }
        if (addButton) {
          addButton.disabled = !available;
          addButton.textContent = addButton.dataset.availableLabel;
        }
        if (buyNowButton) buyNowButton.disabled = !available;
        if (mobileButton) {
          mobileButton.textContent = mobileButton.dataset.availableLabel;
          mobileButton.setAttribute('aria-disabled', String(!available));
        }
        variantOptions.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.value === option.value));
        });
        if (updateMedia && option.dataset.mediaId) activateMedia(gallery, option.dataset.mediaId);

        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', url);
        }
      };

      variantSelect?.addEventListener('change', updateVariantState);

      if (variantSelect && variantButtons && variantOptions.length > 0) {
        updateVariantState({ updateUrl: false, updateMedia: false });
        variantButtons.hidden = false;
        variantSelect.hidden = true;
      }

      productPage.querySelectorAll('[data-pp-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = form?.querySelector('[data-pp-quantity-input]');
          if (!input) return;
          const change = Number(button.dataset.ppQuantityChange || 0);
          const nextValue = Math.max(Number(input.min || 1), Number(input.value || 1) + change);
          input.value = String(nextValue);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  };

  const initProductDescriptions = (scope = document) => {
    scope.querySelectorAll('[data-pp-product-description-source]').forEach((source) => {
      if (source.dataset.ppDescriptionEnhanced === 'true') return;

      const inner = source.children.length === 1 && source.firstElementChild?.hasAttribute('dir')
        ? source.firstElementChild
        : source;
      const mediaSelector = 'img, video, iframe, model-viewer, shopify-model-viewer-ui';
      const hasMeaningfulContent = (node, includeHeadings = true) => {
        if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent.trim());
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        if (!includeHeadings && /^H[2-4]$/.test(node.tagName)) return false;
        return Boolean(node.textContent.replace(/\u00a0/g, ' ').trim() || node.matches(mediaSelector) || node.querySelector(mediaSelector));
      };

      inner.querySelectorAll('p, h2, h3, h4, li, blockquote').forEach((node) => {
        if (!hasMeaningfulContent(node)) node.remove();
      });

      if (![...inner.childNodes].some((node) => hasMeaningfulContent(node))) {
        source.closest('.pp-product-editorial')?.setAttribute('hidden', '');
        source.dataset.ppDescriptionEnhanced = 'true';
        return;
      }

      const nodes = [...inner.childNodes];
      const hasHeadings = nodes.some((node) => node.nodeType === Node.ELEMENT_NODE && /^H[2-4]$/.test(node.tagName));
      if (!hasHeadings) {
        source.dataset.ppDescriptionEnhanced = 'true';
        return;
      }

      const fragment = document.createDocumentFragment();
      let group = document.createElement('section');
      group.className = 'pp-product-editorial__group is-intro';
      group.dataset.ppReveal = '';
      fragment.append(group);

      nodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && /^H[2-4]$/.test(node.tagName)) {
          group = document.createElement('section');
          group.className = 'pp-product-editorial__group';
          group.dataset.ppReveal = '';
          fragment.append(group);
        }
        group.append(node);
      });

      [...fragment.children].forEach((section) => {
        const hasContent = [...section.childNodes].some((node) => hasMeaningfulContent(node, section.classList.contains('is-intro')));
        if (!hasContent) section.remove();
      });

      if (fragment.children.length === 0) {
        source.closest('.pp-product-editorial')?.setAttribute('hidden', '');
        source.dataset.ppDescriptionEnhanced = 'true';
        return;
      }

      inner.replaceChildren(fragment);
      inner.classList.add('is-enhanced');
      source.classList.add('has-enhanced-layout');
      source.dataset.ppDescriptionEnhanced = 'true';
      document.dispatchEvent(new CustomEvent('pp:reveal:refresh', { detail: { scope: source } }));
    });
  };

  const initPredictiveSearch = (scope = document) => {
    scope.querySelectorAll('[data-pp-predictive-form]').forEach((form) => {
      if (form.dataset.ppPredictiveReady === 'true') return;
      const input = form.querySelector('[data-pp-predictive-input]');
      const results = form.querySelector('[data-pp-predictive-results]');
      if (!input || !results) return;
      form.dataset.ppPredictiveReady = 'true';
      const dialog = form.closest('dialog');
      const clearButton = form.querySelector('[data-pp-search-clear]');
      const suggestions = form.querySelector('[data-pp-search-suggestions]');

      let controller;
      let requestToken = 0;
      const close = () => {
        controller?.abort();
        results.hidden = true;
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
      };

      const showStatus = (message) => {
        results.innerHTML = '';
        const status = document.createElement('p');
        status.className = 'pp-predictive__status';
        status.setAttribute('role', 'status');
        status.textContent = message;
        results.append(status);
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };

      const syncInputState = () => {
        const hasValue = input.value.length > 0;
        if (clearButton) clearButton.hidden = !hasValue;
        if (suggestions) suggestions.hidden = input.value.trim().length >= 2;
      };

      const search = debounce(async () => {
        const term = input.value.trim();
        if (term.length < 2) {
          close();
          syncInputState();
          return;
        }

        controller?.abort();
        controller = new AbortController();
        const token = ++requestToken;
        const root = window.Shopify?.routes?.root || '/';
        const params = new URLSearchParams({
          q: term,
          'resources[type]': 'product',
          'resources[limit]': '6',
          section_id: 'predictive-search'
        });

        try {
          showStatus(form.dataset.ppLoadingLabel || 'Loading…');
          let usedLocaleFallback = false;
          let response = await fetch(`${root}search/suggest?${params}`, { signal: controller.signal });
          if (response.status === 417 && root !== '/') {
            response = await fetch(`/search/suggest?${params}`, { signal: controller.signal });
            usedLocaleFallback = true;
          }
          if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const content = parsed.querySelector('[data-pp-predictive-content]');
          if (!content || input.value.trim() !== term || token !== requestToken) return;
          results.innerHTML = content.innerHTML;
          if (usedLocaleFallback) {
            const localePrefix = root.replace(/\/$/, '');
            results.querySelectorAll('a[href^="/"]').forEach((link) => {
              const href = link.getAttribute('href');
              if (href && !href.startsWith(`${localePrefix}/`)) link.setAttribute('href', `${localePrefix}${href}`);
            });
            const heading = results.querySelector('.pp-predictive__heading');
            const listbox = results.querySelector('[role="listbox"]');
            const viewAll = results.querySelector('.pp-predictive__all');
            const status = results.querySelector('.pp-predictive__status');
            if (heading) heading.textContent = form.dataset.ppSuggestedLabel || heading.textContent;
            if (listbox) listbox.setAttribute('aria-label', form.dataset.ppResultsLabel || listbox.getAttribute('aria-label'));
            if (viewAll) viewAll.textContent = form.dataset.ppViewAllLabel || viewAll.textContent;
            if (status) status.textContent = form.dataset.ppNoSuggestionsLabel || status.textContent;
          }
        } catch (error) {
          if (error.name !== 'AbortError' && token === requestToken) {
            showStatus(form.dataset.ppErrorLabel || 'Search is temporarily unavailable.');
          }
        }
      });

      input.addEventListener('input', () => {
        syncInputState();
        search();
      });
      clearButton?.addEventListener('click', () => {
        input.value = '';
        close();
        syncInputState();
        input.focus();
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          if (results.hidden && dialog?.open) dialog.close();
          else close();
        }
        if (event.key === 'ArrowDown' && !results.hidden) {
          const firstLink = results.querySelector('a');
          if (firstLink) {
            event.preventDefault();
            firstLink.focus();
          }
        }
      });
      results.addEventListener('keydown', (event) => {
        const links = [...results.querySelectorAll('a')];
        const index = links.indexOf(document.activeElement);
        if (event.key === 'Escape') {
          close();
          input.focus();
        } else if (event.key === 'ArrowDown' && index >= 0) {
          event.preventDefault();
          (links[index + 1] || links[0])?.focus();
        } else if (event.key === 'ArrowUp' && index >= 0) {
          event.preventDefault();
          (links[index - 1] || input)?.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) close();
      });
      dialog?.addEventListener('close', () => {
        close();
        input.value = '';
        syncInputState();
        const opener = document.querySelector(`[data-pp-open="#${dialog.id}"]`);
        window.requestAnimationFrame(() => opener?.focus({ preventScroll: true }));
      });
      syncInputState();
    });
  };

  const initAddToCartAttention = (scope = document) => {
    const buttons = scope.querySelectorAll('[data-pp-add-to-cart], [data-pp-pdp-add], [data-pp-mobile-buy-button]');
    if (!buttons.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    buttons.forEach((button) => {
      if (button.dataset.ppAttentionObserved === 'true') return;
      button.dataset.ppAttentionObserved = 'true';
      if (reducedMotion) return;

      if (!('IntersectionObserver' in window)) {
        button.classList.add('is-attention-visible');
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-attention-visible', entry.isIntersecting && entry.intersectionRatio >= 0.6);
        });
      }, { threshold: [0, 0.6] });
      observer.observe(button);
    });
  };

  const initSavingsAttention = (scope = document) => {
    const badges = scope.querySelectorAll('[data-pp-savings-pill]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    badges.forEach((badge) => {
      if (badge.dataset.ppSavingsAttentionObserved === 'true') return;
      badge.dataset.ppSavingsAttentionObserved = 'true';
      if (reducedMotion) return;
      if (!('IntersectionObserver' in window)) {
        badge.classList.add('is-savings-attention-visible');
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        badge.classList.toggle('is-savings-attention-visible', entry.isIntersecting && entry.intersectionRatio >= 0.6);
      }, { threshold: [0, 0.6] });
      observer.observe(badge);
    });
  };

  const initBuyNowAttention = (scope = document) => {
    const buttons = scope.querySelectorAll('[data-pp-buy-now], [data-pp-card-intent="buy"]');
    if (!buttons.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    buttons.forEach((button, index) => {
      if (button.dataset.ppBuyNowAttentionObserved === 'true') return;
      button.dataset.ppBuyNowAttentionObserved = 'true';
      if (reducedMotion) return;

      let attentionTimer;
      let initialTimer;
      const staggerDelay = (index % 6) * 160;
      const stopAttention = () => {
        window.clearTimeout(attentionTimer);
        window.clearTimeout(initialTimer);
        button.classList.remove('is-buy-now-attention-visible');
      };
      const runAttention = () => {
        window.clearTimeout(attentionTimer);
        if (button.disabled || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-loading')) {
          button.classList.remove('is-buy-now-attention-visible');
          attentionTimer = window.setTimeout(runAttention, 1450);
          return;
        }
        button.classList.remove('is-buy-now-attention-visible');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => button.classList.add('is-buy-now-attention-visible'));
        });
        attentionTimer = window.setTimeout(runAttention, 1450);
      };

      if (!('IntersectionObserver' in window)) {
        runAttention();
        return;
      }

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          stopAttention();
          initialTimer = window.setTimeout(runAttention, staggerDelay);
        }
        else stopAttention();
      }, { threshold: [0, 0.6] });
      observer.observe(button);
    });
  };

  const initTouchFeedback = (scope = document) => {
    scope.querySelectorAll('.pp-button, .pp-campaign-button').forEach((button) => {
      if (button.dataset.ppTouchFeedbackReady === 'true') return;
      button.dataset.ppTouchFeedbackReady = 'true';
      let releaseTimer;
      const release = () => {
        window.clearTimeout(releaseTimer);
        releaseTimer = window.setTimeout(() => button.classList.remove('is-touch-pressed'), 120);
      };
      button.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || button.matches(':disabled, [aria-disabled="true"]')) return;
        window.clearTimeout(releaseTimer);
        button.classList.add('is-touch-pressed');
      });
      button.addEventListener('pointerup', release);
      button.addEventListener('pointercancel', release);
      button.addEventListener('lostpointercapture', release);
    });
  };

  const initRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-pp-product-recommendations][data-url]').forEach(async (container) => {
      try {
        const response = await fetch(container.dataset.url);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('[data-pp-recommendation-source]');
        const target = container.querySelector('[data-pp-recommendation-content]');
        if (source && target && Number(source.dataset.productCount || 0) > 0) {
          target.innerHTML = source.innerHTML;
          container.hidden = false;
          initProduct(target);
          initMerchantCopyDirection(target);
          initRecommendationRail(container);
          document.dispatchEvent(new CustomEvent('pp:reveal:refresh', { detail: { scope: container } }));
        }
      } catch (_error) {
        // The server-rendered collection or merchant fallback remains available.
      }
    });
  };

  const initProductTabs = (scope = document) => {
    scope.querySelectorAll('[data-pp-product-tabs]').forEach((tabsRoot) => {
      if (tabsRoot.dataset.ppTabsReady === 'true') return;
      tabsRoot.dataset.ppTabsReady = 'true';
      const tabs = [...tabsRoot.querySelectorAll('[data-pp-product-tab]')];
      const panels = [...tabsRoot.querySelectorAll('[data-pp-product-panel]')];
      const activate = (tab, moveFocus = false) => {
        tabs.forEach((candidate) => {
          const selected = candidate === tab;
          candidate.classList.toggle('is-active', selected);
          candidate.setAttribute('aria-selected', String(selected));
          candidate.tabIndex = selected ? 0 : -1;
          const panel = panels.find((item) => item.id === candidate.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        if (moveFocus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          let targetIndex = index;
          const rtl = getComputedStyle(tabsRoot).direction === 'rtl';
          if (event.key === 'Home') targetIndex = 0;
          else if (event.key === 'End') targetIndex = tabs.length - 1;
          else if (event.key === 'ArrowRight') targetIndex = (index + (rtl ? -1 : 1) + tabs.length) % tabs.length;
          else if (event.key === 'ArrowLeft') targetIndex = (index + (rtl ? 1 : -1) + tabs.length) % tabs.length;
          else return;
          event.preventDefault();
          activate(tabs[targetIndex], true);
        });
      });
    });
  };

  const recommendationRails = new WeakMap();
  const initRecommendationRail = (section) => {
    const rail = section.querySelector('[data-pp-recommendation-rail]');
    const controls = section.querySelector('[data-pp-recommendation-controls]');
    const previous = controls?.querySelector('[data-pp-recommendation-previous]');
    const next = controls?.querySelector('[data-pp-recommendation-next]');
    const position = controls?.querySelector('[data-pp-recommendation-position]');
    const current = recommendationRails.get(section);
    if (!rail || !controls) {
      current?.destroy();
      return;
    }
    if (current?.rail === rail && current.controls === controls) return;
    current?.destroy();
    const events = new AbortController();
    let frame;
    let observer;
    const destroy = () => {
      events.abort();
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      delete rail.dataset.ppRailReady;
      if (recommendationRails.get(section)?.rail === rail) recommendationRails.delete(section);
    };
    recommendationRails.set(section, { rail, controls, destroy });
    rail.dataset.ppRailReady = 'true';

    const cards = () => [...rail.children].filter((card) => card.matches('[data-pp-product-card]') && !card.hidden);
    const activeIndex = () => {
      const railRect = rail.getBoundingClientRect();
      const start = rail.dir === 'rtl' || getComputedStyle(rail).direction === 'rtl' ? railRect.right : railRect.left;
      return cards().reduce((best, card, index) => {
        const rect = card.getBoundingClientRect();
        const edge = getComputedStyle(rail).direction === 'rtl' ? rect.right : rect.left;
        const distance = Math.abs(edge - start);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;
    };
    const update = () => {
      if (!rail.isConnected || section.querySelector('[data-pp-recommendation-rail]') !== rail) {
        destroy();
        return;
      }
      const items = cards();
      const index = Math.min(activeIndex(), Math.max(0, items.length - 1));
      const overflows = rail.scrollWidth > rail.clientWidth + 2;
      controls.hidden = !overflows || items.length < 2;
      if (position) position.textContent = `${index + 1} / ${items.length}`;
      if (previous) previous.disabled = Math.abs(rail.scrollLeft) <= 2;
      if (next) next.disabled = Math.abs(rail.scrollLeft) >= rail.scrollWidth - rail.clientWidth - 2;
    };
    const move = (delta) => {
      if (!rail.isConnected || section.querySelector('[data-pp-recommendation-rail]') !== rail) return;
      const items = cards();
      const index = Math.max(0, Math.min(items.length - 1, activeIndex() + delta));
      items[index]?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    };

    previous?.addEventListener('click', () => move(-1), { signal: events.signal });
    next?.addEventListener('click', () => move(1), { signal: events.signal });
    rail.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target !== rail) return;
      event.preventDefault();
      const rtl = getComputedStyle(rail).direction === 'rtl';
      move(event.key === 'ArrowRight' ? (rtl ? -1 : 1) : (rtl ? 1 : -1));
    }, { signal: events.signal });
    rail.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = null; update(); });
    }, { passive: true, signal: events.signal });
    window.addEventListener('resize', update, { passive: true, signal: events.signal });
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(() => {
        if (rail.isConnected) update();
        else destroy();
      });
      observer.observe(rail);
    }
    update();
  };

  const init = (scope = document) => {
    initCollection(scope);
    initCollectionPagination(scope);
    initProduct(scope);
    initProductDescriptions(scope);
    initMerchantCopyDirection(scope);
    initPredictiveSearch(scope);
    initAddToCartAttention(scope);
    initSavingsAttention(scope);
    initBuyNowAttention(scope);
    initTouchFeedback(scope);
    initRecommendations(scope);
    initProductTabs(scope);
    scope.querySelectorAll('[data-pp-product-recommendations]').forEach(initRecommendationRail);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('pp:products:loaded', (event) => init(event.detail.scope));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-pp-collection-pagination]').forEach(root => root.ppCollectionPaginationDestroy?.());
  });
  ['pp:cart:updated', 'pp:cart:opened'].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      document.querySelectorAll('[data-pp-drawer-recommendations]').forEach(initRecommendationRail);
    });
  });
})();
